(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('boops-video-player')) return;

  class BoopsVideoPlayer extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._visible = false;
      this._observer = null;
      this._cleanups = [];
      this._reducedMotion = null;
      // R6: a pill/tap pause is a deliberate user action; it suppresses every
      // automatic resume (IO re-entry, tab refocus, reduced-motion restore) until
      // the user plays again. Programmatic pauses (IO exit, group hand-off,
      // section unload) never set it.
      this._userPaused = false;
      // R2: the one-shot document gesture listener that retries a blocked
      // autoplay on the user's next touch/pointer (iOS Low Power Mode / save-data).
      this._gestureRetry = null;

      this._handlePlay = this._handlePlay.bind(this);
      this._handlePause = this._handlePause.bind(this);
      this._handleEnded = this._handleEnded.bind(this);
      this._handleToggle = this._handleToggle.bind(this);
      this._handleReducedMotion = this._handleReducedMotion.bind(this);
      this._handleSectionUnload = this._handleSectionUnload.bind(this);
      this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
      this._handleGestureRetry = this._handleGestureRetry.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.video = this.querySelector('video');

      const invalidTier = this.getAttribute('data-invalid-tier');
      if (invalidTier && !this.hasAttribute('data-tier-warning-issued')) {
        this.setAttribute('data-tier-warning-issued', '');
        console.warn('[Boops video player] Invalid tier fell back to T3 ENGAGE.', {
          tier: invalidTier,
          sectionId: this.getAttribute('data-section-id') || 'unknown'
        });
      }

      if (!this.video) return;

      this.toggle = this.querySelector('[data-video-toggle]');
      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.video.muted = true;

      this._listen(this.video, 'play', this._handlePlay);
      this._listen(this.video, 'pause', this._handlePause);
      this._listen(this.video, 'ended', this._handleEnded);
      if (this.toggle) this._listen(this.toggle, 'click', this._handleToggle);
      this._listen(document, 'shopify:section:unload', this._handleSectionUnload);
      this._listen(document, 'visibilitychange', this._handleVisibilityChange);

      if (typeof this._reducedMotion.addEventListener === 'function') {
        this._reducedMotion.addEventListener('change', this._handleReducedMotion);
        this._cleanups.push(() => this._reducedMotion.removeEventListener('change', this._handleReducedMotion));
      }

      if (this.hasAttribute('autoplay')) {
        this._observeVisibility();
      }
    }

    disconnectedCallback() {
      this.pause(false);
      this._clearGestureRetry();
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      this._cleanups.forEach((cleanup) => cleanup());
      this._cleanups = [];
      this._connected = false;
    }

    // options.user === true marks a person-driven play (the pill toggle or a
    // gesture on their behalf). Automatic plays (IO, tab refocus, reduced-motion
    // restore) omit it and yield to a user pause (R6) or a playing group peer (R7).
    play(options) {
      if (!this.video || (this._reducedMotion && this._reducedMotion.matches)) return;

      const userInitiated = !!(options && options.user);
      if (userInitiated) {
        this._userPaused = false;
      } else {
        if (this._userPaused) return;
        if (this._hasPlayingGroupPeer()) return;
      }

      this.video.muted = true;
      this._pauseGroupPeers();
      this._queuePosterReveal();

      let playPromise;
      try {
        playPromise = this.video.play();
      } catch (error) {
        this._handleBlockedAutoplay();
        return;
      }

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => this._handleBlockedAutoplay());
      }
    }

    pause(reset) {
      if (!this.video) return;
      this.video.pause();
      if (reset) {
        try {
          this.video.currentTime = 0;
        } catch (error) {
          // Some streaming implementations reject seeks before metadata exists.
        }
      }
    }

    _listen(target, eventName, handler) {
      target.addEventListener(eventName, handler);
      this._cleanups.push(() => target.removeEventListener(eventName, handler));
    }

    _observeVisibility() {
      const threshold = Number.parseFloat(this.getAttribute('data-visibility-threshold') || '0.35');

      if (!('IntersectionObserver' in window)) {
        // Ambient hero motion may play on legacy browsers, but proof/engage
        // video fails closed to its poster when viewport gating is unavailable.
        if (this.getAttribute('data-tier') === 't1') {
          this._visible = true;
          this.play();
        }
        return;
      }

      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this) return;
          // Hysteresis: play at >= threshold, pause only once FULLY offscreen.
          // Pausing in the dead zone (0 < ratio < threshold) tears down the
          // hardware video plane while the video is still visibly on screen —
          // on iOS every plane handoff pops the video's brightness and can
          // blink overlay layers above it (the journey-carousel "dim flash").
          // Fully-offscreen teardown is invisible and keeps the perf gating.
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            this._visible = true;
            this.play();
          } else if (!entry.isIntersecting) {
            this._visible = false;
            // Reset to the start AND re-arm the poster, together: iOS reclaims
            // the offscreen video's hardware plane and drops its decoded frame,
            // so re-entry needs the poster; but a poster over a mid-loop resume
            // point is a visible content jump (and on frame-retaining browsers
            // the poster pops over a live frame). Rewinding makes the poster
            // (the video's own opening frame) and the resume point the same
            // image — re-entry renders a still that simply starts moving, on
            // every platform. Scoped to the offscreen pause only: a user pause
            // or tab switch must keep showing the paused frame, not the poster.
            this.pause(true);
            this.removeAttribute('data-painted');
          }
        });
      }, { threshold: [0, threshold] });
      this._observer.observe(this);
    }

    _pauseGroupPeers() {
      const group = this.getAttribute('group');
      if (!group) return;

      document.querySelectorAll('boops-video-player[group]').forEach((player) => {
        if (player !== this && player.getAttribute('group') === group && typeof player.pause === 'function') {
          player.pause(false);
        }
      });
    }

    // R7: a same-group peer that is already playing wins; an automatic play from
    // this element yields to it (no duplicate fetch/flash, no arbitrary winner).
    _hasPlayingGroupPeer() {
      const group = this.getAttribute('group');
      if (!group) return false;

      let playing = false;
      document.querySelectorAll('boops-video-player[group]').forEach((player) => {
        if (player !== this && player.getAttribute('group') === group && player.video && !player.video.paused) {
          playing = true;
        }
      });
      return playing;
    }

    _handleBlockedAutoplay() {
      this.setAttribute('data-autoplay-blocked', '');
      this._scheduleGestureRetry();
    }

    // R2: register the document gesture listeners once while blocked. They retry
    // this player's autoplay on the user's next pointer/touch — the retry rides
    // their gesture, so iOS Low Power Mode unblocks it. Cleared on success
    // (_handlePlay), on visibility loss, or on disconnect.
    _scheduleGestureRetry() {
      if (this._gestureRetry) return;
      this._gestureRetry = this._handleGestureRetry;
      document.addEventListener('pointerdown', this._gestureRetry, { passive: true });
      document.addEventListener('touchend', this._gestureRetry, { passive: true });
      // Tracked so a disconnect during a blocked state still detaches the listeners.
      this._cleanups.push(() => this._clearGestureRetry());
    }

    _clearGestureRetry() {
      const handler = this._gestureRetry;
      if (!handler) return;
      this._gestureRetry = null;
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('touchend', handler);
    }

    _handleGestureRetry() {
      // Give up quietly if the player no longer wants to autoplay, has scrolled
      // out of view, or the user deliberately paused it.
      if (!this._connected || !this.hasAttribute('autoplay') || !this._visible || this._userPaused) {
        this._clearGestureRetry();
        return;
      }
      this.play();
    }

    _queuePosterReveal() {
      if (!this.video || this.hasAttribute('data-painted')) return;
      const reveal = () => this.setAttribute('data-painted', '');

      if ('requestVideoFrameCallback' in this.video) {
        try {
          this.video.requestVideoFrameCallback(reveal);
          return;
        } catch (error) {
          // Fall through to the loadeddata path.
        }
      }

      if (this.video.readyState >= 2) {
        reveal();
      } else {
        this.video.addEventListener('loadeddata', () => requestAnimationFrame(reveal), { once: true });
      }
    }

    _handlePlay() {
      this.removeAttribute('data-autoplay-blocked');
      this._clearGestureRetry();
      this.setAttribute('data-playing', '');
      this._pauseGroupPeers();
      this._syncToggleLabel();
    }

    _handlePause() {
      this.removeAttribute('data-playing');
      this._syncToggleLabel();
    }

    _handleEnded() {
      this.removeAttribute('data-playing');
      this._syncToggleLabel();
    }

    _handleToggle(event) {
      event.preventDefault();
      if (!this.video) return;
      if (this.video.paused || this.video.ended) {
        // play() on ended media seeks back to the start per spec, so the pill
        // replays a play-once chapter.
        this.play({ user: true });
      } else {
        this._userPaused = true;
        this.pause(false);
      }
    }

    _handleReducedMotion() {
      if (this._reducedMotion.matches) {
        this.pause(false);
      } else if (this._visible && this.hasAttribute('autoplay')) {
        this.play();
      }
    }

    _handleSectionUnload(event) {
      const sectionId = this.getAttribute('data-section-id');
      if (sectionId && event.detail && event.detail.sectionId === sectionId) {
        this.pause(false);
        if (this._observer) this._observer.disconnect();
      }
    }

    _handleVisibilityChange() {
      if (document.hidden) {
        this.pause(false);
      } else if (this._visible && this.hasAttribute('autoplay')) {
        this.play();
      }
    }

    _syncToggleLabel() {
      if (!this.toggle || !this.video) return;
      this.toggle.setAttribute('aria-label', this.video.paused ? 'Play video' : 'Pause video');
    }
  }

  window.customElements.define('boops-video-player', BoopsVideoPlayer);
})();
